import { PropsWithChildren, useCallback, useEffect, useRef, useState, useContext } from 'react';
import { ControlledMenu, MenuItem, useClick } from "@szhsin/react-menu";
import { createPortal } from 'react-dom';
import {
  closestCenter,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  DndContext,
  DragOverlay,
  getFirstCollision,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDroppable,
  UniqueIdentifier,
  useSensors,
  useSensor,
  MeasuringStrategy
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { coordinateGetter as multipleContainersCoordinateGetter } from './multipleContainersKeyboardCoordinates';
import { Identifiable } from '../DragAndDrop/types';
import { ModernCardImage } from '../CardVariantImage/ModernCardImage';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { classNames } from '../../style/common';
import { Button } from '../Control/Button/Button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip/Tooltip';
import { FIREBASE_STORAGE_URL } from '../../app-settings';
import { DropTabProps, PlayDeckProps, PlayState, PlayableVariant, SortableItemProps, animateLayoutChanges, dropAnimation, getDefaultPlayState, CardMenuContext, DropId, TAB_NAME_ZONE_NAME_MAP, TAB_NAME_INDEX_MAP, TAB_INDEX_NAME_MAP, TabIndex, RevealXState, getDefaultRevealXState } from './types';
import { activateAll, getContainerIdByChildId, includes, moveMany, moveToBottomAndSave, moveToTopAndSave, shuffleArrayAndSave, useMountStatus } from './functions';
import { DragItemWrapper } from '../DragAndDrop/DragItemWrapper';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { PlayDeckMenuItems } from './PlayDeckMenu';
import { showFailure, showSuccess } from '../NotificationStack/notificationSlice';
import Modal from '../Modal/Modal';
import NumberInput from '../Control/InputBox/NumberInput';
import { Label } from '../Label/Label';
import "../../style/menu.css";
import { isTruthy } from '../../functions/Common';
import Badge from '../Badge/Badge';

export function PlayDeck(props: PlayDeckProps) {
  const [hasMulliganed, setHasMulliganed] = useState<boolean>(false);
  const [items, setItems] = useState<PlayState>(getDefaultPlayState());
  const [revealXState, setRevealXState] = useState<RevealXState>(getDefaultRevealXState());
  const [isRestartModalOpen, setIsRestartModalOpen] = useState<boolean>(false);
  const [isDeckHidden, setIsDeckHidden] = useState<boolean>(true);

  const [containers, setContainers] = useState(
    Object.keys(items) as UniqueIdentifier[]
  );

  const shuffleArray = (cards: PlayableVariant[]) => {
    for (let i = cards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = cards[i];
      cards[i] = cards[j];
      cards[j] = temp;
    }
  }

  const mulligan = () => {
    const stateCopy: PlayState = JSON.parse(JSON.stringify(items));
    setHasMulliganed(true);

    const newHand = stateCopy.deck.splice(0, 5);
    stateCopy.deck.push(...stateCopy.hand);
    stateCopy.hand = newHand ?? [];

    setItems(stateCopy);
  }

  function moveToStart(
    dragId: UniqueIdentifier,
    destinationId: keyof PlayState,
    stateCopy: PlayState
  ) {
    const match = getDraggableItem(items, dragId);
    if (!match) {
      return;
    }

    match.dragItem.value.isDull = false;
    match.dragItem.value.isFrozen = false;
    match.dragItem.value.counter = 0;

    // remove from source array
    stateCopy[match.source] = stateCopy[match.source].filter(x => isTruthy(x) && x.id !== dragId);
    stateCopy[destinationId] = [match.dragItem, ...stateCopy[destinationId]];
  }

  function moveToEnd(
    dragId: UniqueIdentifier,
    destinationId: keyof PlayState,
    stateCopy: PlayState
  ) {
    const match = getDraggableItem(stateCopy, dragId);
    if (!match) {
      return;
    }

    const isSourceField = match.source.includes("field");
    const isDestinationField = destinationId.includes("field");
    match.dragItem.value.isDull = !isSourceField && isDestinationField;
    match.dragItem.value.isFrozen = false;

    // remove from source array
    stateCopy[match.source] = stateCopy[match.source].filter(x => isTruthy(x) && x.id !== dragId);
    stateCopy[destinationId].push(match.dragItem);
  }

  const restart = () => {
    const stateCopy: PlayState = JSON.parse(JSON.stringify(getDefaultPlayState()));

    const newMainDeck: PlayableVariant[] = [];
    const newLimitBreakDeck: PlayableVariant[] = [];
    Object.entries(props.variantQuantity).forEach(([variantId, quantity]) => {
      const match = props.variantsByIdDictionary[variantId];
      if (!match) {
        return;
      }

      if (match.card.isLB) {
        for (let i = 0; i < quantity; i++) {
          newLimitBreakDeck.push({
            id: `${variantId}-${i.toString()}`,
            value: {
              isDull: false,
              isFrozen: false,
              counter: 0,
              ...match
            }
          });
        }
      } else {
        for (let i = 0; i < quantity; i++) {
          newMainDeck.push({
            id: `${variantId}-${i.toString()}`,
            value: {
              isDull: false,
              isFrozen: false,
              counter: 0,
              ...match
            }
          });
        }
      }
    });

    shuffleArray(newMainDeck);

    const newHand = newMainDeck.splice(0, 5);

    stateCopy.hand = newHand;
    stateCopy.deck = newMainDeck;
    stateCopy.limitBreak = newLimitBreakDeck;
    setItems(stateCopy);
    setHasMulliganed(false);
    setIsRestartModalOpen(false);
  }

  const onRevealXClick = () => {
    setRevealXState(old => ({ ...old, isOpen: true }));
  }

  const closeRevealX = () => {
    setRevealXState(getDefaultRevealXState());
  }

  const revealX = () => {
    const zone = TAB_NAME_ZONE_NAME_MAP[revealXState.sourceTab];
    const cards = items[zone];

    if (!revealXState.quantity) {
      showFailure("Must supply a quantity to reveal X", 6);
      closeRevealX();
      return;
    }

    if (!cards || !cards?.length || revealXState.quantity > cards.length) {
      showFailure(`Not enough cards to reveal ${revealXState.quantity}`, 6);
      closeRevealX();
      return;
    }

    moveMany(cards.slice(0, revealXState.quantity), zone, "revealed", items, setItems, setHasMulliganed);
    showSuccess(`${revealXState.quantity} revealed`, 2000);
    setSelectedTab(TAB_NAME_INDEX_MAP["revealed-tab"]);
    closeRevealX();
  }

  useEffect(() => {
    restart();
  }, [props.variantQuantity]);

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [sourceContainerId, setSourceContainerId] = useState<UniqueIdentifier | null>(null);
  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);
  const [selectedTab, setSelectedTab] = useState<TabIndex>(0);

  /**
   * Custom collision detection strategy optimized for multiple containers
   *
   * - First, find any droppable containers intersecting with the pointer.
   * - If there are none, find intersecting containers with the active draggable.
   * - If there are no intersecting containers, return the last matched intersection
   *
   */
  const collisionDetectionStrategy: CollisionDetection = useCallback(
    (args) => {
      if (activeId && activeId in items) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(
            (container) => 
              isTruthy(container) &&
              container.id in items
          ),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections = pointerIntersections.length > 0
        ? pointerIntersections // If there are droppables intersecting with the pointer, return those
        : rectIntersection(args);

      let overId = getFirstCollision(intersections, 'id');

      if (overId != null) {
        if (overId in items) {
          const containerItems = items[overId as keyof PlayState];

          // If a container is matched and it contains items (columns 'A', 'B', 'C')
          if (containerItems.length > 0) {
            // Return the closest droppable within that container
            overId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) =>
                  isTruthy(container) &&
                  container.id !== overId &&
                  includes(containerItems, container.id)
              ),
            })[0]?.id;
          }
        }

        lastOverId.current = overId;

        return [{ id: overId }];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, items]
  );

  const [clonedItems, setClonedItems] = useState<PlayState | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        delay: 250,
        distance: 8
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: multipleContainersCoordinateGetter
    })
  );

  const onDragCancel = () => {
    if (clonedItems) {
      // Reset items to their original state in case items have been
      // Dragged across containers
      setItems(clonedItems);
    }

    setActiveId(null);
    setClonedItems(null);
  };

  const onCardDoubleClick = (id: UniqueIdentifier, sourceId: keyof PlayState) => {
    if (!sourceId.includes("field")) {
      return;
    }

    const copy: PlayState = JSON.parse(JSON.stringify(items));
    const container = copy[sourceId];
    const item = container.find(x => x.id === id);
    if (!item) {
      return;
    }

    item.value.isDull = !item.value.isDull;
    setItems(copy);
  }

  const getSourceContainerId = (data: any | undefined) => {
    const value = data as { containerId: string } | undefined;
    if (!value) {
      return null;
    }

    return value.containerId;
  }

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [items]);

  // On drag start, clone items in the container. This will ensure we can reset to starting state if need be. Also set active id. Active is the container being dragged from?
  return (
    <CardMenuContext.Provider
      value={{
        setHasMulliganed: setHasMulliganed,
        setSelectedTab: setSelectedTab,
        setState: setItems,
        state: items,
        selectedTabIndex: selectedTab,
        onRevealX: onRevealXClick,
        setIsDeckHidden: setIsDeckHidden,
        isDeckHidden: isDeckHidden,
        onCardDoubleClick: onCardDoubleClick
      }}
    >
      <DndContext
        autoScroll={false}
        sensors={sensors}
        collisionDetection={collisionDetectionStrategy}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
        onDragStart={({ active }) => {
          const currentSourceContainerId = getSourceContainerId(active.data.current);
          setSourceContainerId(previous => previous || currentSourceContainerId);
          setActiveId(active.id);
          setClonedItems(items);
        }}
        onDragOver={({ active, over }) => {

          const overId = over?.id;

          if (overId == null || active.id in items) {
            return;
          }

          const overContainer = getContainerIdByChildId(overId as DropId, items);
          const activeContainer = getContainerIdByChildId(active.id as DropId, items);

          if (!overContainer || !activeContainer) {
            return;
          }

          if (overId === `${activeContainer}-tab`) {
            return;
          }

          if (activeContainer !== overContainer) {
            setItems((items) => {
              const activeItems = items[activeContainer as keyof PlayState];
              const overItems = items[overContainer as keyof PlayState];
              const overIndex = overItems.findIndex(x => x.id === overId);
              const activeIndex = activeItems.findIndex(x => x.id === active.id);

              let newIndex: number;

              if (overId in items) {
                newIndex = overItems.length + 1;
              } else {
                const isBelowOverItem =
                  over &&
                  active.rect.current.translated &&
                  active.rect.current.translated.top >
                  over.rect.top + over.rect.height;

                const modifier = isBelowOverItem ? 1 : 0;

                newIndex =
                  overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
              }

              recentlyMovedToNewContainer.current = true;

              return {
                ...items,
                [activeContainer]: items[activeContainer as keyof PlayState].filter(
                  (item) => isTruthy(item) && item.id !== active.id
                ),
                [overContainer]: [
                  ...items[overContainer as keyof PlayState].slice(0, newIndex),
                  items[activeContainer as keyof PlayState][activeIndex],
                  ...items[overContainer as keyof PlayState].slice(
                    newIndex,
                    items[overContainer as keyof PlayState].length
                  ),
                ],
              };
            });
          }
        }}
        onDragEnd={({ active, over }) => {
          // active: The thing being dragged.
          // over: The container cursor is over.

          try {
            const destinationContainerId = getContainerIdByChildId(active.id as DropId, items);
            const overId = over?.id;

            const source = sourceContainerId?.toString();
            const destination = destinationContainerId?.toString();

            if (!sourceContainerId || !destinationContainerId || !overId || !source || !destination) {
              return;
            }

            const activeIndex = items[destinationContainerId as keyof PlayState].findIndex(x => x.id === active.id);
            let overIndex = items[destinationContainerId as keyof PlayState].findIndex(x => x.id === overId);

            if (overIndex <= 0) {
              overIndex = 0;
            }

            if (source.includes("field") && !destination.includes("field")) {
              const item = items[destinationContainerId as keyof PlayState][activeIndex];
              if (!item) {
                return;
              }

              item.value.isDull = false;
              item.value.isFrozen = false;
              item.value.counter = 0;
            }

            if (!source.includes("field") && destination.includes("field")) {
              // Something is being dragged onto the field from outside the field.

              const item = items[destinationContainerId as keyof PlayState][activeIndex];
              if (!item) {
                return;
              }

              if (item.value.card.type === "Backup") {
                item.value.isDull = true;
                item.value.isFrozen = false;
              }
            }

            if (activeIndex !== overIndex) {
              // The item is being reordered.
              setItems((items) => {
                return {
                  ...items,
                  [destinationContainerId]: arrayMove(
                    items[destinationContainerId as keyof PlayState],
                    activeIndex,
                    overIndex
                  )
                }
              });

              return;
            }
          } finally {
            setActiveId(null);
            setSourceContainerId(null);
          }
        }}
        onDragCancel={onDragCancel}
      >
        <div className='flex flex-col select-none'>
          <SortableContext
            items={[...containers]}
            strategy={
              horizontalListSortingStrategy
            }
          >
            <div className='mb-2 gap-2 grid grid-cols-3 md:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10 3xl:grid-cols-12'>
              <Button size='xs' onClick={() => setIsRestartModalOpen(true)}>Restart</Button>
              {
                !hasMulliganed &&
                <Button size='xs' onClick={() => mulligan()}>Mulligan</Button>
              }

              <Button size='xs' onClick={() => activateAll(items, setItems)}>Activate</Button>

              <Button size='xs' onClick={() => {
                const copy: PlayState = JSON.parse(JSON.stringify(items));
                moveToStart(items.deck[0].id, "hand", copy);
                setSelectedTab(TAB_NAME_INDEX_MAP["hand-tab"]);
                setItems(copy);
              }}>Draw</Button>
            </div>

            <div className='flex flex-col bg-secondary border border-color-border/65 rounded-md'>
              <DropContainer
                containerId='field1'
                items={items}
                classNames={{ default: "h-[70px] max-h-[70px] !min-h-[70px] m-2 !bg-secondary !border-none", hover: "!bg-tertiary", list: "justify-center" }} />

              <DropContainer
                containerId='field2'
                items={items}
                classNames={{ default: "h-[70px] max-h-[70px] !min-h-[70px] m-2 !bg-secondary !border-none", hover: "!bg-tertiary", list: "justify-center" }} />

              <DropContainer
                containerId='field3'
                items={items}
                classNames={{ default: "h-[70px] max-h-[70px] !min-h-[70px] m-2 !bg-secondary !border-none", hover: "!bg-tertiary", list: "justify-center" }} />
            </div>

            <TabGroup className="w-full max-w-full" selectedIndex={selectedTab} onChange={(index) => setSelectedTab(index as TabIndex)}>
              <TabList className="mt-2 overflow-visible gap-2 grid grid-cols-3 md:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10 3xl:grid-cols-12 mb-2">
                <DropTab id={"hand-tab"} text='Hand' isActive={selectedTab === 0} hideMenu />
                <DropTab id={"deck-tab"} text='Deck' isActive={selectedTab === 1} />
                <DropTab id={"limitBreak-tab"} text='LB' isActive={selectedTab === 2} hideMenu />
                <DropTab id={"breakZone-tab"} text='Break' isActive={selectedTab === 3} />
                <DropTab id={"removedFromGame-tab"} text='Removed' isActive={selectedTab === 4} hideMenu />
                <DropTab id={"damageZone-tab"} text={`Damage ${items.damageZone.length}`} isActive={selectedTab === 5} hideMenu />
                <DropTab id={"revealed-tab"} text='Revealed' isActive={selectedTab === 6} />
              </TabList>
              <TabPanels className="!overflow-visible">
                <TabPanel>
                  <DropContainer
                    containerId='hand'
                    items={items} />
                </TabPanel>
                <TabPanel>
                  <DropContainer
                    hidden={isDeckHidden}
                    containerId='deck'
                    items={items}
                    classNames={{ list: "scrollbar scrollbar-thumb-muted" }} />
                </TabPanel>
                <TabPanel>
                  <DropContainer
                    containerId='limitBreak'
                    items={items} />
                </TabPanel>
                <TabPanel>
                  <DropContainer
                    showEmptyMessage={true}
                    containerId='breakZone'
                    items={items} />
                </TabPanel>
                <TabPanel>
                  <DropContainer
                    showEmptyMessage={true}
                    containerId='removedFromGame'
                    items={items} />
                </TabPanel>
                <TabPanel>
                  <DropContainer
                    showEmptyMessage={true}
                    containerId='damageZone'
                    items={items} />
                </TabPanel>
                <TabPanel>
                  <DropContainer
                    showEmptyMessage={true}
                    containerId='revealed'
                    items={items} />
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </SortableContext>
        </div>

        {createPortal(
          <DragOverlay dropAnimation={dropAnimation}>
            {activeId
              ? containers.includes(activeId)
                ? null
                : <SortableItemDragOverlay id={activeId} items={items} hidden={isDeckHidden} />
              : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>

      <Modal isOpen={revealXState.isOpen} onOpenChange={(isOpen) => closeRevealX()} title='Reveal X' classNames={{ dialogPanel: "!max-w-[400px]" }}>
        <Label label='Quantity' classNames={{ container: "mt-8" }}>
          <NumberInput
            inputStyle='underline'
            value={revealXState.quantity}
            onChange={(value) => {
              setRevealXState(old => ({ ...old, quantity: value }));
            }} />
        </Label>
        <div className="space-x-2 mt-8">
          <Button
            onClick={() => {
              closeRevealX();
            }}
            color="component">
            Cancel
          </Button>
          <Button
            disabled={!revealXState.quantity}
            onClick={() => {
              revealX();
            }}
            color="emphasis">
            Reveal
          </Button>
        </div>
      </Modal>

      <Modal isOpen={isRestartModalOpen} onOpenChange={(isOpen) => setIsRestartModalOpen(false)} title='Restart?' classNames={{ dialogPanel: "!max-w-[400px]" }}>
        <div className='mt-8'>
          Are you sure you want to restart?
        </div>

        <div className="space-x-2 mt-8">
          <Button
            onClick={() => {
              setIsRestartModalOpen(false);
            }}
            color="component">
            Cancel
          </Button>
          <Button
            onClick={() => {
              restart();
              showSuccess("Restarted", 2000);
            }}
            color="emphasis">
            Restart
          </Button>
        </div>
      </Modal>
    </CardMenuContext.Provider>
  );
}

function DropContainer(props: {
  containerId: keyof PlayState,
  items: PlayState,
  showEmptyMessage?: boolean,
  hidden?: boolean,
  classNames?: { default?: string, hover?: string, list?: string }
}) {
  return (
    <DroppableContainer
      key={props.containerId}
      id={props.containerId}
      items={props.items[props.containerId as keyof PlayState]}
      classNames={props.classNames}
    >
      <SortableContext items={props.items[props.containerId as keyof PlayState]} strategy={horizontalListSortingStrategy}>
        {
          (props.showEmptyMessage && !props.items[props.containerId as keyof PlayState].length) &&
          <li className='flex flex-1 items-center justify-center text-xl font-semibold p-8 text-contrast-tertiary'>
            <div>Empty</div>
          </li>
        }
        {
          props.items[props.containerId as keyof PlayState].map((value, index) => {
            if (!value || !value?.id) {
              return undefined;
            }

            return (
              <SortableItem
                key={value.id}
                item={value}
                index={index}
                hidden={props.hidden}
                containerId={props.containerId}
              />
            );
          })
        }
      </SortableContext>
    </DroppableContainer>
  )
}

function SortableItem(props: SortableItemProps) {
  const {
    setNodeRef,
    listeners,
    isDragging,
    isSorting,
    transform,
    transition,
  } = useSortable({
    id: props.item.id,
    data: {
      containerId: props.containerId
    }
  });

  const mounted = useMountStatus();
  const mountedWhileDragging = isDragging && !mounted;
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const {
    onCardDoubleClick
  } = useContext(CardMenuContext);

  const ref = useRef(null);
  const anchorProps = useClick(isMenuOpen, setIsMenuOpen);

  return (
    <li
      key={props.item.id}
      className={classNames(
        'relative touch-none',
        props.item.value.isDull && "w-[70px]"
      )}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      <DragItemWrapper
        ref={props.disabled ? undefined : setNodeRef}
        value={props.item.id}
        dragging={isDragging}
        sorting={isSorting}
        index={props.index}
        transition={transition}
        transform={transform}
        fadeIn={mountedWhileDragging}
        isRotated={props.item.value.isDull}
        listeners={listeners}
      >
        {
          props.item.value.counter > 0 &&
          <div className={classNames("absolute top-0 right-0 font-semibold z-10 flex")}>
            <Badge className='font-semibold text-center shadow !p-1 justify-center' style={{ borderRadius: "50%", height: "15px", width: "15px" }}>{props.item.value.counter}</Badge>
          </div>
        }
        {
          (isHovered || isMenuOpen) &&
          <div className={classNames("absolute flex", false ? "flex-row left-[14%] bottom-0" : "flex-col bottom-0 left-0")}>
            <button type="button" className="z-30 relative !h-8 !w-[50px] bg-secondary text-xs text-contrast-primary rounded-b-md flex items-center justify-center border border-color-border/80" ref={ref} {...anchorProps}>
              Actions
            </button>

            <ControlledMenu
              portal
              className={"relative z-50"}
              menuClassName={"shadow shadow-lg shadow-black/30  border !border-color-border/20"}
              state={isMenuOpen ? 'open' : 'closed'}
              anchorRef={ref}
              onClose={() => setIsMenuOpen(false)}
            >
              <PlayDeckMenuItems
                card={props.item}
                menuType={props.containerId}
                sourceId={props.containerId}
                onItemClick={() => {
                  setIsMenuOpen(false);
                  setIsHovered(false);
                }} />
            </ControlledMenu>
          </div>
        }

        {
          props.hidden
            ? <img
              className="absolute top-0 rounded-md"
              style={{ aspectRatio: "143/200" }}
              src={`${FIREBASE_STORAGE_URL}/images/cards/card-back.jpg`} />
            : <Tooltip>
              <TooltipTrigger className='w-full'>
                <div style={{ aspectRatio: "143/200" }}>
                  <img
                    onDoubleClick={(event) => {
                      onCardDoubleClick(props.item.id, props.containerId);
                    }}
                    style={{ aspectRatio: "143/200", zIndex: "1" }}
                    src={`${FIREBASE_STORAGE_URL}/images/cards/${props.item.value.imageId}.jpg`}
                    alt={`${props.item.value.card?.name} image`}
                    loading="lazy"
                    className={classNames(
                      `relative w-full rounded-md`,
                      (props.item.value.isFrozen && props.containerId.includes("field")) && "outline outline-emphasis outline-4 -outline-offset-4"
                    )} />

                  <img
                    className="absolute top-0 p-[2px] rounded-md"
                    style={{ aspectRatio: "143/200" }}
                    src={`${FIREBASE_STORAGE_URL}/images/cards/card-back.jpg`} />
                </div>
              </TooltipTrigger>
              <TooltipContent className="z-[200]">
                <img
                  style={{ aspectRatio: "143/200", zIndex: "200" }}
                  src={`${FIREBASE_STORAGE_URL}/images/cards/${props.item.value.imageId}.jpg`}
                  alt={`${props.item.value?.card?.name} image`}
                  loading="lazy"
                  className={classNames(
                    `relative rounded-lg w-[300px]`
                  )} />
              </TooltipContent>
            </Tooltip>
        }
      </DragItemWrapper>
    </li>
  );
}

function SortableItemDragOverlay(props: {
  id: UniqueIdentifier,
  items: PlayState,
  hidden?: boolean
}) {
  const match = getDraggableItem(props.items, props.id);

  if (!match) {
    return <></>;
  }

  return (
    <DragItemWrapper
      value={match.dragItem.id}
      isRotated={match.dragItem.value.isDull}
      dragOverlay
    >
      {
        (match.source === "deck" && props.hidden)
          ? <img
            className="absolute top-0 rounded-lg"
            style={{ aspectRatio: "143/200" }}
            src={`${FIREBASE_STORAGE_URL}/images/cards/card-back.jpg`} />
          : <ModernCardImage hidePlaceholderText cardName={match.dragItem.value.card?.name ?? ""} imagePath={match.dragItem.value.imageId} serial={match.dragItem.value.serial} />
      }
    </DragItemWrapper>
  );
}

function DroppableContainer(props: PropsWithChildren & {
  disabled?: boolean,
  id: keyof PlayState,
  items: Identifiable[],
  classNames?: {
    default?: string,
    hover?: string,
    list?: string
  }
}) {
  const {
    active,
    over,
    setNodeRef
  } = useSortable({
    id: props.id,
    data: {
      type: 'container',
      children: props.items,
    },
    animateLayoutChanges,
  });

  const isOverContainer = over
    ? (props.id === over.id && active?.data.current?.type !== 'container') ||
    includes(props.items, over.id)
    : false;

  return (
    <div
      ref={setNodeRef}
      className={classNames(
        props.classNames?.default,
        isOverContainer && props.classNames?.hover,
        "overflow-visible"
      )}
    >
      <ul className={classNames("flex gap-2 overflow-x-auto pb-10 overflow-y-visible", props.classNames?.list)}>
        {props.children}
      </ul>
    </div>
  );
}

function DropTab(props: DropTabProps) {
  const {
    setState,
    state,
    setHasMulliganed,
    setSelectedTab,
    selectedTabIndex,
    onRevealX,
    setIsDeckHidden,
    isDeckHidden
  } = useContext(CardMenuContext);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const ref = useRef(null);
  const anchorProps = useClick(isMenuOpen, setIsMenuOpen);
  const { setNodeRef, isOver } = useDroppable({
    id: props.id
  });

  return (
    <div
      className={classNames(
        "flex h-[36px] items-stretch"
      )}
    >
      <Tab
        ref={setNodeRef}
        className={classNames(
          'flex-1 pl-4 min-w-0 text-left text-xs flex items-center !h-full z-10',
          isOver ? "!outline !outline-emphasis" : "",
          "data-[selected]:bg-tertiary hover:data-[selected]:bg-tertiary data-[selected]:cursor-default bg-secondary hover:bg-tertiary rounded-l-md shadow h-max font-semibold text-sm",
          props.hideMenu && "rounded-r-md"
        )}
      >
        <div>{props.text}</div>
      </Tab>

      {
        !props.hideMenu &&
        <div>
          <button
            type="button"
            className={classNames(
              'py-2 px-2 bg-secondary hover:bg-tertiary rounded-r-md data-[selected]:bg-tertiary shadow',
              props.isActive && "bg-tertiary hover:bg-tertiary/80"
            )}
            ref={ref}
            {...anchorProps}
          >
            <EllipsisVerticalIcon className='h-5 w-5' />
          </button>

          <ControlledMenu
            portal
            className={"relative z-50"}
            menuClassName={"shadow shadow-lg shadow-black/30  border !border-color-border/20"}
            state={isMenuOpen ? 'open' : 'closed'}
            anchorRef={ref}
            onClose={() => setIsMenuOpen(false)}
          >
            {
              props.id === "deck-tab" &&
              <MenuItem
                onClick={() => {
                  const sourceId = TAB_NAME_ZONE_NAME_MAP[props.id];
                  const items = state[sourceId];

                  if (!items?.length) {
                    return;
                  }

                  moveToTopAndSave(items[0], sourceId, "damageZone", state, setState, setHasMulliganed);
                  setSelectedTab(TAB_NAME_INDEX_MAP["damageZone-tab"]);
                  showSuccess(`Took damage`, 2000);
                }}
              >
                Take Damage
              </MenuItem>
            }
            {
              (props.id === "deck-tab" && isDeckHidden) &&
              <MenuItem
                onClick={() => {
                  setIsDeckHidden(false);
                  setSelectedTab(TAB_NAME_INDEX_MAP["deck-tab"]);
                }}
              >
                View
              </MenuItem>
            }
            {
              (props.id === "deck-tab" && !isDeckHidden) &&
              <MenuItem
                onClick={() => {
                  setIsDeckHidden(true);
                  setSelectedTab(TAB_NAME_INDEX_MAP["deck-tab"]);
                }}
              >
                Hide
              </MenuItem>
            }
            {
              props.id === "deck-tab" &&
              <MenuItem
                onClick={() => {
                  const selectedTab = TAB_INDEX_NAME_MAP[selectedTabIndex];
                  const sourceId = TAB_NAME_ZONE_NAME_MAP[props.id];
                  const items = state[sourceId];

                  if (!items?.length) {
                    return;
                  }

                  moveToBottomAndSave(items[0], sourceId, "revealed", state, setState, setHasMulliganed);
                  setSelectedTab(TAB_NAME_INDEX_MAP["revealed-tab"]);
                  showSuccess(`Revealed 1`, 2000);
                }}
              >
                Reveal 1
              </MenuItem>
            }
            {
              props.id === "deck-tab" &&
              <MenuItem
                onClick={() => {
                  onRevealX();
                }}
              >
                Reveal X
              </MenuItem>
            }
            {
              props.id === "deck-tab" &&
              <MenuItem
                onClick={() => {
                  shuffleArrayAndSave(state, "deck", setState);
                  showSuccess(`Shuffled`, 2000);
                }}
              >
                Shuffle
              </MenuItem>
            }
            {
              (props.id === "breakZone-tab" || props.id === "revealed-tab") &&
              <MenuItem
                onClick={() => {
                  const sourceId = TAB_NAME_ZONE_NAME_MAP[props.id];
                  const items = state[sourceId];
                  moveMany(items, sourceId, "removedFromGame", state, setState, setHasMulliganed);
                  showSuccess(`${items.length} cards removed`, 2000);
                }}
              >
                Remove All
              </MenuItem>
            }
            {
              props.id === "breakZone-tab" &&
              <MenuItem
                onClick={() => {
                  const sourceId = TAB_NAME_ZONE_NAME_MAP[props.id];
                  let items = state[sourceId];
                  items = items.filter(x => isTruthy(x) && x.value.card.type !== "Summon");
                  moveMany(items, sourceId, "removedFromGame", state, setState, setHasMulliganed);
                  showSuccess(`${items.length} characters removed`, 2000);
                }}
              >
                Remove All Characters
              </MenuItem>
            }

            {
              (props.id === "revealed-tab") &&
              <MenuItem
                onClick={() => {
                  const sourceId = TAB_NAME_ZONE_NAME_MAP[props.id];
                  const items = state[sourceId];

                  moveMany(items, sourceId, "deck", state, setState, setHasMulliganed, false);
                  showSuccess(`${items.length} cards to bottom`, 2000);
                  setSelectedTab(TAB_NAME_INDEX_MAP["hand-tab"]);
                }}
              >
                All to Bottom
              </MenuItem>
            }
            {
              (props.id === "revealed-tab") &&
              <MenuItem
                onClick={() => {
                  const sourceId = TAB_NAME_ZONE_NAME_MAP[props.id];
                  const items = state[sourceId];
                  moveMany(items, sourceId, "deck", state, setState, setHasMulliganed, true);
                  showSuccess(`${items.length} cards to top`, 2000);
                  setSelectedTab(TAB_NAME_INDEX_MAP["hand-tab"]);
                }}
              >
                All to Top
              </MenuItem>
            }
          </ControlledMenu>
        </div>
      }
    </div>
  )
}

export function getDraggableItem(state: PlayState, id: UniqueIdentifier): {
  dragItem: PlayableVariant,
  source: keyof PlayState
} | null {
  const keys: (keyof PlayState)[] = Object.keys(state) as (keyof PlayState)[];
  for (const key of keys) {
    const items: PlayableVariant[] = state[key];
    const match = items.find(x => x.id === id);

    if (match) {
      return {
        dragItem: match,
        source: key
      };
    }
  }

  return null;
}